import { useState, useCallback } from "react";
import {
	getCriteria,
	createCriterion,
	updateCriterion,
	deleteCriterion,
	reorderCriteria,
} from "../api/criteria-api";
import type {
	EvaluationCriterion,
	CreateCriterionPayload,
	UpdateCriterionPayload,
} from "../types/criteria.type";

interface UseEvaluationCriteriaOptions {
	rfxId: number | undefined;
}

export type SaveStatus = "idle" | "saving" | "saved" | "error";

interface UseEvaluationCriteriaResult {
	criteria: EvaluationCriterion[];
	error: string | null;
	isLoading: boolean;
	isSaving: boolean;
	saveStatus: SaveStatus;
	totalWeight: number;
	isWeightValid: boolean;
	fetchCriteria: () => Promise<void>;
	addCriterion: (payload: CreateCriterionPayload) => Promise<EvaluationCriterion | null>;
	editCriterion: (criterionId: number, payload: UpdateCriterionPayload) => Promise<EvaluationCriterion | null>;
	removeCriterion: (criterionId: number) => Promise<boolean>;
	reorder: (fromIndex: number, toIndex: number) => Promise<void>;
	setCriteria: React.Dispatch<React.SetStateAction<EvaluationCriterion[]>>;
}

export const useEvaluationCriteria = ({
	rfxId,
}: UseEvaluationCriteriaOptions): UseEvaluationCriteriaResult => {
	const [criteria, setCriteria] = useState<EvaluationCriterion[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

	const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);
	const isWeightValid = totalWeight === 100;

	const fetchCriteria = useCallback(async (): Promise<void> => {
		if (!rfxId) {
			return;
		}
		setIsLoading(true);
		setError(null);
		try {
			const data = await getCriteria(rfxId);
			setCriteria(data);
		} catch {
			setError("Failed to load evaluation criteria");
		} finally {
			setIsLoading(false);
		}
	}, [rfxId]);

	const addCriterion = useCallback(
		async (payload: CreateCriterionPayload): Promise<EvaluationCriterion | null> => {
			if (!rfxId) {
				return null;
			}
			setIsSaving(true);
			setError(null);
			try {
				const newCriterion = await createCriterion(rfxId, payload);
				setCriteria((prev) => [...prev, newCriterion]);
				return newCriterion;
			} catch {
				setError("Failed to add criterion");
				return null;
			} finally {
				setIsSaving(false);
			}
		},
		[rfxId]
	);

	const editCriterion = useCallback(
		async (
			criterionId: number,
			payload: UpdateCriterionPayload
		): Promise<EvaluationCriterion | null> => {
			if (!rfxId) {
				return null;
			}
			setIsSaving(true);
			setError(null);
			try {
				const updated = await updateCriterion(rfxId, criterionId, payload);
				setCriteria((prev) =>
					prev.map((c) => (c.id === criterionId ? updated : c))
				);
				return updated;
			} catch {
				setError("Failed to update criterion");
				return null;
			} finally {
				setIsSaving(false);
			}
		},
		[rfxId]
	);

	const removeCriterion = useCallback(
		async (criterionId: number): Promise<boolean> => {
			if (!rfxId) {
				return false;
			}
			setIsSaving(true);
			setError(null);
			try {
				await deleteCriterion(rfxId, criterionId);
				setCriteria((prev) => prev.filter((c) => c.id !== criterionId));
				return true;
			} catch {
				setError("Failed to delete criterion");
				return false;
			} finally {
				setIsSaving(false);
			}
		},
		[rfxId]
	);

	const reorder = useCallback(
		async (fromIndex: number, toIndex: number): Promise<void> => {
			if (!rfxId || fromIndex === toIndex) return;

			// Optimistic reorder
			setCriteria((prev) => {
				const next = [...prev];
				const [moved] = next.splice(fromIndex, 1);
				next.splice(toIndex, 0, moved);
				return next;
			});

			const order = (() => {
				const next = [...criteria];
				const [moved] = next.splice(fromIndex, 1);
				next.splice(toIndex, 0, moved);
				return next.map((c, i) => ({ id: c.id, sort_order: i + 1 }));
			})();

			setSaveStatus("saving");
			try {
				const updated = await reorderCriteria(rfxId, order);
				setCriteria(updated);
				setSaveStatus("saved");
			} catch {
				setError("Failed to reorder criteria");
				setSaveStatus("error");
				await fetchCriteria();
			}
		},
		[rfxId, criteria, fetchCriteria]
	);

	return {
		criteria,
		error,
		isLoading,
		isSaving,
		saveStatus,
		totalWeight,
		isWeightValid,
		fetchCriteria,
		addCriterion,
		editCriterion,
		removeCriterion,
		reorder,
		setCriteria,
	};
};
